import React, { useContext, useState } from 'react';
import { MergedPanel } from './StatRuneSwitch';
import RuneBoard from './RuneStar';
import RuneDetail from './RuneDetail';
import RelicDetail, { RelicBadge } from './RelicDetail';
import ArtifactDetail, { ArtifactBadge } from './ArtifactDetail';
import { Rune, Relic, Artifact, peakRune } from './runeEngine';

/**
 * **룬 여섯 개를 게임 룬 창처럼** — 별, 그 오른쪽에 몹에 새긴 유물(육각), 판이나 유물을 누르면
 * 그 옆에 창. 한 판, 같은 어두운 바탕. 전투력·최고속 둘 다 이걸로 그린다. 폰에서는 줄이 좁아
 * 아래로 내려간다.
 *
 * 룬 옵티마이저와 전투기록 창(그 전투에서 쓴 룬)이 **같은 것**을 쓴다 — 같은 룬이 화면마다
 * 다르게 보이면 안 된다.
 *
 * **아티팩트**(둥근 칸 둘)는 `artifacts` 를 줄 때만 별 왼쪽에 그린다 — 게임 몹 창과 같은 자리다.
 * 옵티마이저는 주지 않는다(사용자 지시 — 거기서는 전투력에만 넣는다). 전투기록은 그 전투에 낀
 * 아티팩트를 알아서 준다(사용자 지시: 아티팩트도 보이게).
 */
/**
 * 폰에서 룬 판·옵션 창 폭 — [스탯|룬] 칸은 둘 중 큰 창 높이에 맞춰지는데, 폭을 다 쓰면 룬 창이 길어져 스탯 창 밑이 비고
 * 창 전체가 길어졌다(사용자: 창 아래 여백 확보). 묶어서 두 창 높이를 엇비슷하게 맞춘다.
 */
const MOBILE_W = 250;

export default function RuneBuildPanel({ runes, objective, relic, artifacts, isMobile, asIs = false, initialPick = null }: {
    runes: Rune[]; objective: 'power' | 'speed'; relic: Relic | null; isMobile: boolean;
    /** 낀 아티팩트 — 주면 별 왼쪽에 둥근 칸 둘(위 속성 · 아래 유형) */
    artifacts?: Artifact[];
    /** 룬 그대로만 — 변환 예정·연마 여유를 그리지 않는다 (전투기록용, `RuneDetail` 참고) */
    asIs?: boolean;
    /** 처음부터 골라 둘 룬 자리 — 전투기록은 몹을 누르면 1번 룬이 바로 보이게 한다(사용자 지시) */
    initialPick?: number | null;
}) {
    /** 누른 것 — 룬 자리(1~6), 유물('relic'), 아티팩트('art1' 속성 · 'art2' 유형) */
    const [picked, setPicked] = useState<number | 'relic' | 'art1' | 'art2' | null>(
        initialPick && runes.some((r) => r.slot === initialPick) ? initialPick : null
    );
    const toggle = (k: number | 'relic' | 'art1' | 'art2') => setPicked((cur) => (cur === k ? null : k));
    const pickedRune = typeof picked === 'number' ? (runes.find((r) => r.slot === picked) || null) : null;
    const pickedRelic = picked === 'relic' ? relic : null;
    const artAttr = artifacts?.find((a) => a.type === 1) || null;
    const artStyle = artifacts?.find((a) => a.type === 2) || null;
    const pickedArt = picked === 'art1' ? artAttr : picked === 'art2' ? artStyle : null;
    /**
     * **폰은 위아래로** — 별(아티팩트·유물 포함) 아래에 고른 것의 옵션 창. 나란히 두면 폰 폭에서 옵션 창이
     * 잘렸다(사용자 지시). 폭을 다 쓰니 별도 크게 그린다. 스탯 창과는 `StatRuneSwitch` 로 바꿔 본다.
     */
    const pick = toggle;
    const close = () => setPicked(null);
    // 폰은 작게 — [스탯|룬] 전환 칸에서 스탯 창과 같은 크기로 들어간다(사용자 지시: 크기 통일·창 여유)
    /**
     * 스탯 창과 한 상자에 들어가면(데스크톱) 바탕은 상자가 그리고, 옵션 창은 **별 아래로** — 스탯 오른쪽 칸이
     * 별과 옵션 창을 나란히 둘 만큼 넓지 않다.
     */
    const merged = useContext(MergedPanel);
    const column = isMobile || merged;
    return (
        <div style={{
            ...(merged ? {} : {
                margin: isMobile ? 0 : '10px 0 6px', padding: isMobile ? 5 : 12, borderRadius: 16,
                background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
            }),
            // 한 줄로 — 폰에서도 아래로 안 내려간다(별을 줄여 자리를 만든다).
            // 위아래는 가운데 맞춤 — 오른쪽 룬 창이 더 길면 왼쪽 칸이 위에 붙어 어긋나 보였다
            display: 'flex', gap: isMobile ? 6 : merged ? 12 : 14, alignItems: merged ? 'stretch' : 'center', flexWrap: 'nowrap',
            flexDirection: column ? 'column' : 'row'
        }}>
            {/*
              룬 판 — 게임 룬 창 그림 한 장 위에 문양·아티팩트·유물(`RuneBoard`). 폰과 합친 상자에서는 칸 폭을 다
              쓰고, 따로 놓인 데스크톱(옵티마이저)은 옆에 옵션 창이 오니 폭을 정해 둔다.
            */}
            {/* 폰은 별 판을 가운데로 — 왼쪽에 붙어 아래 옵션 창(가운데)과 어긋났다 (사용자 2026-10-28 "둘다 가운데정렬") */}
            <div style={isMobile ? { alignSelf: 'center' } : { display: 'contents' }}>
                <RuneBoard
                    slots={runes.map((r) => ({ slot: r.slot, set: r.set, ancient: r.ancient }))}
                    width={isMobile ? MOBILE_W : column ? undefined : 440} tight={column}
                    picked={typeof picked === 'number' ? picked : null}
                    onPick={(n) => pick(n)}
                    artifacts={artifacts ? (size) => [
                        <ArtifactBadge artifact={artAttr} kind={1} size={size} picked={picked === 'art1'} onClick={() => pick('art1')} />,
                        <ArtifactBadge artifact={artStyle} kind={2} size={size} picked={picked === 'art2'} onClick={() => pick('art2')} />
                    ] : undefined}
                    relic={(size) => <RelicBadge relic={relic} size={size} picked={picked === 'relic'} onClick={() => pick('relic')} />}
                />
            </div>
            {/*
              폰은 옵션 창 폭을 **묶어 둔다** — 옵션 창은 폭에 맞춰 커지므로, 넓은 폰(폴드를 편 화면)에서는 키가 커져
              [스탯|룬] 칸이 화면 밑까지 찼다(사용자 지시: 창 밑에 여백). 묶으면 어느 폰에서나 같은 높이다.
            */}
            <div style={{
                flex: merged ? '1 1 auto' : column ? '0 0 auto' : '1 1 220px', width: column ? '100%' : undefined, maxWidth: isMobile ? MOBILE_W : undefined,
                minWidth: 0, alignSelf: isMobile ? 'center' : 'stretch', display: 'flex', alignItems: 'center',
                // 칸 구분 — 별 칸처럼 테두리를 두른 한 칸
                ...(merged ? { boxSizing: 'border-box', minHeight: 120, padding: '10px 14px', borderRadius: 14, background: 'rgba(0, 0, 0, 0.22)', border: '1px solid #5a3d24' } : {})
            }}>
                {pickedRune ? (
                    <div style={{ width: '100%' }}>
                        <RuneDetail
                            rune={pickedRune} peak={asIs ? undefined : peakRune(pickedRune, objective)} asIs={asIs}
                            isMobile={isMobile} bare onClose={close}
                        />
                    </div>
                ) : pickedRelic ? (
                    <div style={{ width: '100%' }}>
                        <RelicDetail relic={pickedRelic} isMobile={isMobile} onClose={close} />
                    </div>
                ) : pickedArt ? (
                    <div style={{ width: '100%' }}>
                        <ArtifactDetail artifact={pickedArt} isMobile={isMobile} onClose={close} />
                    </div>
                ) : (
                    <div style={{ width: '100%', textAlign: 'center', fontSize: isMobile ? 11 : 12, color: '#b8a58c', fontWeight: 600, padding: isMobile ? '18px 0' : 0 }}>
                        {isMobile
                            ? `위에서 룬이나 ${artifacts ? '아티팩트, ' : ''}유물을 눌러서 옵션을 확인해주세요`
                            : (artifacts ? '룬이나 아티팩트, 유물을 눌러서 옵션을 확인해주세요' : '룬이나 유물을 눌러서 옵션을 확인해주세요')}
                    </div>
                )}
            </div>
        </div>
    );
}
